import {
  Business as BusinessIcon,
  PersonAdd as ConvertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  MoreVert as MoreVertIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import ReactDOM from 'react-dom';
import { Cell, Pie, PieChart, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

// Константы статусов
const LEAD_STATUSES = {
  new: { label: 'Новый', color: '#2196F3', icon: '🆕' },
  contacted: { label: 'В контакте', color: '#FF9800', icon: '📞' },
  qualified: { label: 'Квалифицирован', color: '#4CAF50', icon: '✅' },
  converted: { label: 'Конвертирован', color: '#9C27B0', icon: '🎯' },
  rejected: { label: 'Отклонен', color: '#F44336', icon: '❌' }
};

const LEAD_SOURCES = {
  website: { label: 'Сайт', icon: '🌐' },
  ads: { label: 'Реклама', icon: '📢' },
  referral: { label: 'Рекомендация', icon: '👥' },
  cold_call: { label: 'Холодный звонок', icon: '📞' },
  event: { label: 'Мероприятие', icon: '🎪' },
  social: { label: 'Соц.сети', icon: '📱' },
  other: { label: 'Другое', icon: '❓' }
};

const SOURCE_COLORS = {
  website: '#42a5f5',
  ads: '#ffb74d',
  referral: '#66bb6a',
  cold_call: '#ab47bc',
  event: '#26c6da',
  social: '#ef5350',
  other: '#bdbdbd'
};

const LeadCard = ({ lead, onEdit, onDelete, onConvert }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const getScoreColor = (score) => {
    if (score >= 80) return '#4CAF50';
    if (score >= 60) return '#FF9800';
    if (score >= 40) return '#FFC107';
    return '#F44336';
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit(lead);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete(lead);
  };

  const handleConvert = () => {
    handleMenuClose();
    onConvert(lead);
  };

  return (
    <Card
      sx={{
        mb: 1,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        cursor: 'grab',
        '&:hover': {
          background: 'rgba(255, 255, 255, 0.15)',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        },
        transition: 'all 0.2s ease'
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Заголовок карточки */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
              {getInitials(lead.name)}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'white' }}>
                {lead.name}
              </Typography>
              {lead.position && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {lead.position}
                </Typography>
              )}
            </Box>
          </Box>

          <IconButton size="small" onClick={handleMenuClick}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Компания */}
        {lead.company && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <BusinessIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {lead.company}
            </Typography>
          </Box>
        )}

        {/* Контактная информация */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          {lead.email && (
            <Tooltip title={lead.email}>
              <IconButton size="small" color="primary">
                <EmailIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {lead.phone && (
            <Tooltip title={lead.phone}>
              <IconButton size="small" color="primary">
                <PhoneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Источник */}
        <Chip
          label={LEAD_SOURCES[lead.source]?.label || lead.source}
          size="small"
          sx={{
            mb: 1,
            fontSize: '0.7rem',
            height: 22,
            px: 0.75,
            color: 'rgba(255,255,255,0.95)',
            border: `1px solid ${SOURCE_COLORS[lead.source] || '#bdbdbd'}`,
            background: 'rgba(255,255,255,0.10)'
          }}
          variant="outlined"
          icon={<span style={{ fontSize: '0.8rem' }}>{LEAD_SOURCES[lead.source]?.icon}</span>}
        />

        {/* Рейтинг */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Рейтинг:
          </Typography>
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={lead.score || 0}
              sx={{
                height: 6,
                borderRadius: 3,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getScoreColor(lead.score || 0),
                  borderRadius: 3
                }
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: getScoreColor(lead.score || 0), fontWeight: 600 }}>
            {lead.score || 0}
          </Typography>
        </Box>

        {/* Теги */}
        {lead.tags && lead.tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {lead.tags.slice(0, 3).map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.6rem', height: 16 }}
              />
            ))}
            {lead.tags.length > 3 && (
              <Chip
                label={`+${lead.tags.length - 3}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.6rem', height: 16 }}
              />
            )}
          </Box>
        )}

        {/* Меню действий */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Редактировать
          </MenuItem>
          {lead.status !== 'converted' && (
            <MenuItem onClick={handleConvert}>
              <ConvertIcon fontSize="small" sx={{ mr: 1 }} />
              Конвертировать
            </MenuItem>
          )}
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Удалить
          </MenuItem>
        </Menu>
      </CardContent>
    </Card>
  );
};

// Обёртка для точного выравнивания курсора и карточки при перетаскивании
const LeadDraggableWrapper = ({ dragProvided, dragSnapshot, children }) => {
  const getPortalContainer = React.useCallback(() => {
    let node = document.getElementById('dnd-portal');
    if (!node) {
      node = document.createElement('div');
      node.id = 'dnd-portal';
      document.body.appendChild(node);
    }
    return node;
  }, []);

  const baseStyle = dragProvided.draggableProps.style || {};
  const combinedStyle = { ...baseStyle };

  const content = (
    <Box
      ref={dragProvided.innerRef}
      {...dragProvided.draggableProps}
      {...dragProvided.dragHandleProps}
      style={combinedStyle}
      sx={{ opacity: dragSnapshot.isDragging ? 0.98 : 1, zIndex: dragSnapshot.isDragging ? 2000 : 'auto' }}
    >
      {children}
    </Box>
  );

  return dragSnapshot.isDragging ? ReactDOM.createPortal(content, getPortalContainer()) : content;
};

const KanbanColumn = ({ status, leads, onLeadAction }) => {
  const statusConfig = LEAD_STATUSES[status];
  const leadsInStatus = leads.filter(lead => lead.status === status);

  const metrics = useMemo(() => {
    const total = (leadsInStatus || []).length;
    const sumScore = (leadsInStatus || []).reduce((s, l) => s + (Number(l.score) || 0), 0);
    const avgScore = total > 0 ? Math.round((sumScore / total) * 10) / 10 : 0;
    const bySource = {};
    (leadsInStatus || []).forEach((l) => {
      const src = l.source || 'other';
      bySource[src] = (bySource[src] || 0) + 1;
    });
    const topSources = Object.entries(bySource)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    const rest = Math.max(0, Object.keys(bySource).length - topSources.length);
    return { total, avgScore, topSources, rest };
  }, [leadsInStatus]);

  return (
    <Paper
      sx={{
        minHeight: 400,
        p: 2,
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderTop: `4px solid ${statusConfig.color}`,
        minWidth: 280
      }}
    >
      {/* Заголовок колонки */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ fontSize: '1.2rem' }}>{statusConfig.icon}</span>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            {statusConfig.label}
          </Typography>
        </Box>
        <Badge badgeContent={leadsInStatus.length} color="primary" />
      </Box>

      {/* Мини-метрики по колонке */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={`Avg score: ${metrics.avgScore}`} sx={{ height: 22, px: 0.75, color: 'rgba(255,255,255,0.95)', background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.22)' }} />
        {metrics.topSources.map(([src, count]) => (
          <Chip
            key={src}
            size="small"
            label={`${LEAD_SOURCES[src]?.icon || '❓'} ${LEAD_SOURCES[src]?.label || src}: ${count}`}
            sx={{ height: 22, px: 0.75, color: 'rgba(255,255,255,0.95)', border: `1px solid ${SOURCE_COLORS[src] || '#bdbdbd'}`, background: 'rgba(255,255,255,0.10)' }}
            variant="outlined"
          />
        ))}
        {metrics.rest > 0 && (
          <Chip size="small" label={`+${metrics.rest}`} sx={{ height: 22, px: 0.75, color: 'rgba(255,255,255,0.95)', background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.22)' }} />
        )}
      </Box>

      {/* Зона для dnd карточек */}
      <Droppable droppableId={status}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minHeight: 300,
              p: 0.5,
              borderRadius: 1,
              background: snapshot.isDraggingOver ? 'rgba(255,255,255,0.06)' : 'transparent'
            }}
          >
            {leadsInStatus.map((lead, index) => (
              <Draggable draggableId={lead.id} index={index} key={lead.id}>
                {(dragProvided, dragSnapshot) => (
                  <LeadDraggableWrapper
                    dragProvided={dragProvided}
                    dragSnapshot={dragSnapshot}
                  >
                    <LeadCard
                      lead={lead}
                      onEdit={onLeadAction.onEdit}
                      onDelete={onLeadAction.onDelete}
                      onConvert={onLeadAction.onConvert}
                    />
                  </LeadDraggableWrapper>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {leadsInStatus.length === 0 && (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '2px dashed rgba(255, 255, 255, 0.2)',
                  borderRadius: 2,
                  color: 'text.secondary'
                }}
              >
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Нет лидов в этом статусе
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Droppable>
    </Paper>
  );
};

const LeadKanban = ({ leads, onLeadAction, onFilterSource, onFilterStatus }) => {
  const statusOrder = ['new', 'contacted', 'qualified', 'converted', 'rejected'];

  const overallStats = useMemo(() => {
    const total = (leads || []).length;
    const sumScore = (leads || []).reduce((s, l) => s + (Number(l.score) || 0), 0);
    const avgScore = total > 0 ? Math.round((sumScore / total) * 10) / 10 : 0;
    const converted = (leads || []).filter((l) => l.status === 'converted').length;
    const conversionRate = total > 0 ? Math.round((converted / total) * 1000) / 10 : 0;
    const bySource = {};
    (leads || []).forEach((l) => {
      const src = l.source || 'other';
      bySource[src] = (bySource[src] || 0) + 1;
    });
    // Готовим данные для диаграммы
    const sourceData = Object.entries(bySource).map(([src, count]) => ({
      name: LEAD_SOURCES[src]?.label || src,
      key: src,
      value: count,
      color: SOURCE_COLORS[src] || '#bdbdbd'
    }));
    return { total, avgScore, bySource, sourceData, conversionRate, converted };
  }, [leads]);

  const statusMetrics = useMemo(() => {
    const metrics = {};
    statusOrder.forEach((status) => {
      const arr = (leads || []).filter((l) => l.status === status);
      const total = arr.length;
      const sumScore = arr.reduce((s, l) => s + (Number(l.score) || 0), 0);
      const avgScore = total > 0 ? Math.round((sumScore / total) * 10) / 10 : 0;
      metrics[status] = { total, avgScore };
    });
    return metrics;
  }, [leads]);

  const statusBuckets = useMemo(() => {
    const buckets = Object.fromEntries(statusOrder.map((s) => [s, []]));
    (leads || []).forEach((l) => {
      if (buckets[l.status]) buckets[l.status].push(l);
      else buckets['new'].push(l);
    });
    return buckets;
  }, [leads]);

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    const targetStatus = destination.droppableId;
    if (onLeadAction && typeof onLeadAction.onStatusChange === 'function') {
      onLeadAction.onStatusChange(draggableId, targetStatus);
    }
  };

  return (
    <>
      {/* Сводка по доске */}
      <Paper sx={{
        mb: 2,
        p: 2.5,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.06))',
        backdropFilter: 'blur(14px)',
        border: '1px solid rgba(255, 255, 255, 0.18)',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 700 }}>
              Всего: {overallStats.total}
            </Typography>
            <Chip size="small" label={`Средний рейтинг: ${overallStats.avgScore}`} sx={{ height: 26, px: 1, color: 'rgba(255,255,255,0.95)', border: '1px solid rgba(255,255,255,0.22)', backdropFilter: 'blur(10px)', background: 'rgba(255,255,255,0.12)' }} />
            <Chip size="small" label={`Конверсия: ${overallStats.conversionRate}% (${overallStats.converted})`} sx={{ height: 26, px: 1, color: 'rgba(255,255,255,0.95)', border: '1px solid rgba(255,255,255,0.22)', backdropFilter: 'blur(10px)', background: 'rgba(255,255,255,0.12)' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {overallStats.sourceData.map((d) => (
              <Chip
                key={d.key}
                size="small"
                label={`${LEAD_SOURCES[d.key]?.icon || '❓'} ${d.name}: ${d.value}`}
                onClick={() => onFilterSource && onFilterSource(d.key)}
                sx={{ height: 22, borderColor: d.color, cursor: 'pointer', '&:hover': { background: 'rgba(255,255,255,0.12)' } }}
                variant="outlined"
              />
            ))}
          </Box>
          <Box sx={{ width: 180, height: 120 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart style={{ filter: 'drop-shadow(0 6px 22px rgba(0,0,0,0.28))' }}>
                <Pie
                  data={overallStats.sourceData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={36}
                  outerRadius={58}
                  paddingAngle={3}
                  labelLine={false}
                >
                  {overallStats.sourceData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      stroke="rgba(255,255,255,0.35)"
                      strokeWidth={1.4}
                    />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(value, name) => [value, name]}
                  contentStyle={{
                    background: 'rgba(255,255,255,0.10)',
                    border: '1px solid rgba(255,255,255,0.22)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 8,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                    color: 'rgba(255,255,255,0.95)'
                  }}
                  itemStyle={{ color: 'rgba(255,255,255,0.95)' }}
                  labelStyle={{ color: 'rgba(255,255,255,0.85)' }}
                  cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        {/* Мини-сводка по статусам */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {statusOrder.map((st) => (
            <Chip
              key={st}
              size="small"
              label={`${LEAD_STATUSES[st].icon} ${LEAD_STATUSES[st].label}: ${statusMetrics[st].total} (avg ${statusMetrics[st].avgScore})`}
              onClick={() => onFilterStatus && onFilterStatus(st)}
              sx={{
                height: 24,
                px: 0.75,
                color: 'rgba(255,255,255,0.95)',
                borderLeft: `4px solid ${LEAD_STATUSES[st].color}`,
                border: '1px solid rgba(255,255,255,0.20)',
                background: 'rgba(255,255,255,0.10)',
                cursor: 'pointer',
                '&:hover': { background: 'rgba(255,255,255,0.16)' }
              }}
              variant="outlined"
            />
          ))}
        </Box>
      </Paper>

      <DragDropContext onDragEnd={onDragEnd}>
        <Box sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 2,
          minHeight: 500
        }}>
          {statusOrder.map((status) => (
            <KanbanColumn
              key={status}
              status={status}
              leads={statusBuckets[status]}
              onLeadAction={onLeadAction}
            />
          ))}
        </Box>
      </DragDropContext>
    </>
  );
};

export default LeadKanban;